import { createContext, useContext, useEffect, useRef, useState } from "react";

interface RootContextProps {
    rootDivRef: React.RefObject<HTMLDivElement | null>,
    isOpened: boolean,
    setIsOpened: React.Dispatch<React.SetStateAction<boolean>>
    redirectUrl?: string
}

const RootDefaults: RootContextProps = {
    rootDivRef: { current: null },
    isOpened: false,
    setIsOpened: () => {}
}

const RootDivContext = createContext<RootContextProps>(RootDefaults)

interface RootProviderProps extends React.PropsWithChildren<{}> {
}

export const RootDivProvider: React.FC<RootProviderProps> = ({ children }) => {

    const rootDivRef = useRef<HTMLDivElement | null>(null);
    const [isOpened, setIsOpened] = useState(false);

    useEffect(() => {
        const rootDiv = document.getElementById('react-job-application-form-root');
        if (rootDiv) { 
            rootDivRef.current = rootDiv as HTMLDivElement;
            rootDivRef.current.getAttribute('data-open') === '1' ? setIsOpened(true) : setIsOpened(false);
        }
    }, []);

    useEffect(() => {

        const element = rootDivRef.current;
        if (!element) return;

        // Callback function to execute when mutations are observed
        const callback = (mutationsList: any, _observer: any) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-open') {
                    element.getAttribute('data-open') === '1' ? setIsOpened(true) : setIsOpened(false)
                }
            }
        };

        // Create an observer instance linked to the callback function
        const observer = new MutationObserver(callback);

        // Start observing the target element for configured mutations
        observer.observe(element, {
            attributes: true, // Configure it to listen to attribute changes
            attributeFilter: ['data-open'] // Listen for changes specifically to the data-open attribute
        });

        // Clean up the observer on component unmount
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (isOpened) {
            document.body.style.overflowY = 'hidden'
            rootDivRef.current?.querySelector('.form-container')?.classList.add('open')
        } else {
            document.body.style.overflowY = 'auto'
            rootDivRef.current?.querySelector('.form-container')?.classList.remove('open')
        }
    }, [isOpened])


    return (
        <RootDivContext.Provider value={{ rootDivRef, isOpened, setIsOpened }}>
            {children}
        </RootDivContext.Provider>
    )
}

export const useRootDivContext = () => {
    const context = useContext(RootDivContext)
    if (!context) {
        throw new Error('useFormContext must be used within a FormProvider')
    }
    return context
}

export default RootDivContext